import React from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentStatus, AttachmentType } from '../../../../../../../../../../@types/types'
import { InitState } from '../../../../../../../../../../redux/reducers/root/initialState'
import { useAppSelector } from '../../../../../../../../../../app/hooks'
import { Button } from 'is24-corecss'
import styles from '../common/MultimediaButtonCommon.module.css'
import MultimediaButtonContent from '../common/MultimediaButtonContent'
import { IconS24CameraVideo48 } from '@is24/cosma-ui-icons'
import MultimediaVideoButton from './MultimediaVideoButton'
import MultimediaButtonLoadingContent from '../common/MultimediaButtonLoadingContent'
import { MultimediaButtonTypes } from '../common/mediaButtonContentProvider'

type MultimediaVideoButtonStateWrapperTypes = Omit<MultimediaButtonTypes, 'isFloorPlan'>

export const MultimediaVideoButtonStateWrapper: React.FC<MultimediaVideoButtonStateWrapperTypes> = ({
  allowedFileType,
  flowInformation,
  entryMode,
}) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'wizard.screen.multimediaNew.buttons' })
  const attachment = useAppSelector((state: InitState) =>
    state.multimediaFeature.attachments.find((it) => it.attachmentType === AttachmentType.VIDEO)
  )
  const videoInfo = useAppSelector((state: InitState) => state.multimediaFeature.videoInfo[0])
  const realEstateId = useAppSelector((state: InitState) => state.insertionFeature.insertionIdentification.realEstateId)

  const processingVideoInfo =
    videoInfo?.videoInfo?.processingStatus === 'PROCESSING' || videoInfo?.videoInfo?.processingStatus === 'INCOMING'
  const uploadingVideo = attachment?.state === AttachmentStatus.UPLOADING
  const videoUploadedMessage = t(`common.attachmentUploadedMessage`)

  const shouldRenderNoVideoYetButton = !attachment
  const shouldRenderUploadingVideoButton = attachment && uploadingVideo && !processingVideoInfo
  const shouldRenderVideoProcessingButton = attachment && videoInfo && processingVideoInfo
  const shouldRenderUploadedVideoButton = attachment && attachment.state === AttachmentStatus.PERSISTED
  const listingNotSaved = !realEstateId

  return (
    <>
      {shouldRenderNoVideoYetButton && (
        <MultimediaVideoButton
          allowedFileType={allowedFileType}
          flowInformation={flowInformation}
          entryMode={entryMode}
          realEstateId={realEstateId}
          disabled={
            shouldRenderUploadingVideoButton ||
            shouldRenderVideoProcessingButton ||
            shouldRenderUploadedVideoButton ||
            listingNotSaved
          }
        />
      )}

      {shouldRenderUploadingVideoButton && (
        <UploadingContent
          subtitle={t(`common.attachmentUploadingMessage`)}
          testId='uploading-video'
          percent={attachment?.file!.percent!}
        />
      )}

      {shouldRenderVideoProcessingButton && (
        <UploadingContent
          subtitle={t(`common.attachmentProcessingMessage`)}
          testId='processing-video'
          percent={attachment?.file!.percent!}
        />
      )}

      {shouldRenderUploadedVideoButton && (
        <Button
          variant='outlineWeak'
          align='start'
          className={`${styles.button} ${styles.buttonDisabled}`}
          data-testid='uploaded-video'
          disabled
        >
          <MultimediaButtonContent
            Icon={IconS24CameraVideo48}
            title={t('videos.title')}
            subtitle={videoUploadedMessage}
          />
        </Button>
      )}
    </>
  )
}

function UploadingContent({ subtitle, testId, percent }: { subtitle: string; testId: string; percent: number }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'wizard.screen.multimediaNew.buttons.videos' })
  return (
    <Button
      variant='outlineWeak'
      align='start'
      disabled
      className={`${styles.button} ${styles.buttonDisabled} ${styles.loaderContainer}`}
      data-testid={testId}
    >
      <MultimediaButtonLoadingContent title={t('title')} subtitle={`${subtitle} ${percent} %`} />
    </Button>
  )
}

export default MultimediaVideoButtonStateWrapper
